import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { graphql } from 'gatsby'
import { Parallax, ParallaxLayer } from 'react-spring/renderprops-addons'
import Fade from 'react-reveal/Fade'
import Layout from '../components/Layout'
import ImageCard from '../components/ImageCard'
import Footer from '../components/Footer'
import PageHeader from '../elements/PageHeader'
import Bubbles from '../components/Bubbles'
import BgSeparator from '../images/bg-separator.svg'
import BgSeparator2 from '../images/bg-separator2.svg'
import BgParticles from '../components/BgParticles'

const positions = {
  top: {
    desktop: 'top: 0; left: 60%;',
    mobile: 'display: none;',
  },
  left: {
    desktop: 'left: 5%; top: -30%;',
    mobile: 'left: 18%; top: -32%;',
  },
  right: {
    desktop: 'right: 0; top: -30%;',
    mobile: 'right: 0; top: -40%;',
  },
}

const BubblesContainer = styled.div`
  ${tw`absolute`};
  ${props => positions[props.position].desktop};

  @media screen and (max-width: 768px) {
    ${props => positions[props.position].mobile};
  }
`

const BgImg = styled.img`
  ${tw`m-0`} position: absolute;
  z-index: -2;
  width: 100%;
  height: 400px;
  bottom: 0;

  @media screen and (min-width: 768px) {
    height: 750px;
  }
`

const IndexContainer = styled.section`
  ${tw`relative h-screen pb-32`};
`

const PageHeadingContainer = styled.header`
  ${tw`w-full flex flex-col items-center px-8 text-center z-30`};
  position: absolute;
  transform: translateY(-50%);
  top: 50%;
`

const PageHeading = styled.div`
  ${tw`flex relative inline-block font-serif z-50 text-center xl:text-left mx-8 font-bold leading-normal text-5xl md:text-5xl py-12 xl:py-0 text-center`};

  @media screen and (min-width: 992px) {
    font-size: 72px;
  }
`

const HeadingSpan = styled.span`
  ${tw`relative block sm:inline-block pr-2`}

  &:after {
    content: '';
    ${tw`bg-green-accent`}
    display: inline-block;
    width: 100%;
    height: 12px;
    position: absolute;
    top: 1.04em;
    left: 0;
    z-index: -1;
  }
`

const PageSubHeading = styled.p`
  ${tw`font-sans text-grey-darkest z-50 text-xl lg:text-2xl lg:mt-4`};
  line-height: 175%;
`

const HighlightedLink = styled.a`
  ${tw`relative no-underline text-grey-darkest font-medium md:font-normal`}
  font-style: italic;

  &:after {
    content: '';
    ${tw`bg-black`}
    display: inline-block;
    width: 100%;
    height: 0;
    position: absolute;
    top: 1.375em;
    left: 0;
    z-index: -1;
    transition: 0.2s linear;

    @media screen and (min-width: 768px) {
      height: 1px;
    }
  }

  &:hover:after {
    top: 0.75em;
  }
`

const ImageSection = styled.section`
  ${tw`absolute w-full z-10`}
  ${props => (props.pin === 'top' ? tw`pin-t` : tw`pin-b`)};
`

const ParticlesContainer = styled.div`
  ${tw`absolute pin-t pin-x mt-8 `}
  height: 100%;
`

const ImageLayer = ({ children, offset, pin, data }) => {
  const background = offset % 2 === 0 ? BgSeparator2 : BgSeparator

  return (
    <>
      <ParallaxLayer offset={offset} speed={1} factor={0.6}>
        <BgImg src={background} />
      </ParallaxLayer>
      <ParallaxLayer
        offset={offset}
        speed={0.5}
        factor={1}
        style={{ zIndex: 10 }}
      >
        <Fade bottom>
          <ImageSection pin={pin}>
            <ImageCard data={data} index={offset} factor={0.8} />
          </ImageSection>
        </Fade>
        {children}
      </ParallaxLayer>
    </>
  )
}

const IndexPage = ({ data }) => {
  return (
    <Layout>
      <Parallax pages={8.5}>
        <IndexContainer>
          <ParallaxLayer
            offset={0}
            speed={0.5}
            factor={1}
            style={{ zIndex: 10 }}
          >
            <PageHeader />
            <PageHeadingContainer>
              <PageHeading>
                <HeadingSpan>Currently</HeadingSpan>
                <HeadingSpan>World's</HeadingSpan>
                <HeadingSpan>best City</HeadingSpan>
              </PageHeading>
              <PageSubHeading>
                Industrial centre of{' '}
                <HighlightedLink href="https://en.wikipedia.org/wiki/West_Pomeranian_Voivodeship">
                  West Pomeranian Voivodeship
                </HighlightedLink>
              </PageSubHeading>
            </PageHeadingContainer>
            <ParticlesContainer>
              <BgParticles />
            </ParticlesContainer>
          </ParallaxLayer>
          <ImageLayer
            offset={1}
            factor={2}
            pin="top"
            data={data.allMdx.edges[0]}
          />
          <ImageLayer
            offset={1}
            pin="top"
            factor={0.5}
            data={data.allMdx.edges[0]}
          >
            <BubblesContainer position="left">
              <Bubbles position="left" />
            </BubblesContainer>
          </ImageLayer>
          <ImageLayer offset={2} pin="top" data={data.allMdx.edges[1]}>
            <BubblesContainer position="right">
              <Bubbles position="right" />
            </BubblesContainer>
          </ImageLayer>
          <ImageLayer offset={3} pin="top" data={data.allMdx.edges[2]}>
            <BubblesContainer position="left">
              <Bubbles position="left" />
            </BubblesContainer>
          </ImageLayer>
          <ImageLayer offset={4} pin="top" data={data.allMdx.edges[3]}>
            <BubblesContainer position="right">
              <Bubbles position="right" />
            </BubblesContainer>
          </ImageLayer>
          <ImageLayer offset={5} pin="top" data={data.allMdx.edges[4]}>
            <BubblesContainer position="left">
              <Bubbles position="left" />
            </BubblesContainer>
          </ImageLayer>
          <ImageLayer offset={6} pin="top" data={data.allMdx.edges[5]}>
            <BubblesContainer position="right">
              <Bubbles position="right" />
            </BubblesContainer>
          </ImageLayer>
          <ImageLayer offset={7} pin="top" data={data.allMdx.edges[6]} />
          <ParallaxLayer offset={8} speed={0.2} factor={0.4}>
            <Footer />
          </ParallaxLayer>
        </IndexContainer>
      </Parallax>
    </Layout>
  )
}

export default IndexPage

IndexPage.propTypes = {
  data: PropTypes.shape({
    allMdx: PropTypes.shape({
      edges: PropTypes.array.isRequired,
    }),
  }).isRequired,
}

export const pageQuery = graphql`
  query HomeQuery {
    allMdx {
      edges {
        node {
          fields {
            slug
          }
          frontmatter {
            title
            areas
            cover {
              childImageSharp {
                fluid(maxWidth: 760, quality: 90) {
                  ...GatsbyImageSharpFluid_withWebp
                }
              }
            }
          }
        }
      }
    }
  }
`
